/**
 * COLONY SIDEBAR
 *
 * Left navigation sidebar connected to real stores.
 * Replaces TopNavbar as the primary navigation element.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { useEscapeClose } from "@/hooks/useEscapeClose";
import {
  Search,
  Plus,
  Bot,
  Globe,
  FolderKanban,
  ChevronDown,
  ChevronRight,
  Settings,
  LayoutGrid,
  GitBranch,
  Terminal,
  MessageSquare,
  KeyRound,
  FileText,
  Eye,
  Workflow,
  PanelLeftClose,
  PanelLeft,
  SquarePen,
  Check,
  LogOut,
  CreditCard,
  CalendarDays,
  BarChart3,
  Tag,
  GripVertical,
  ArrowUpDown,
} from "lucide-react";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "@/components/ui/context-menu";
import { ColonyAvatar } from "@/components/colony/ColonyAvatar";
import { ColonyTooltip } from "@/components/colony/ColonyTooltip";
import { StatusIcon, mapProjectStatus } from "@/components/colony/StatusIcon";
import { useSidebarStore, type ProjectSubTab } from "@/stores/sidebarStore";
import { useContentStore, type ContentTab } from "@/stores/contentStore";
import { useUIStore } from "@/stores/uiStore";
import { useWorldStore } from "@/stores/worldStore";
import { useWorkspaceStore, getWorkspaceType, getWorkspaceRoute, setWorkspaceRoute, getWorkspaceTab, setWorkspaceTab } from "@/stores/workspaceStore";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingStore } from "@/stores/billingStore";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import { isTauri } from "@/hooks/usePlatform";
import { ProjectCreationFlow } from "@/features/projects/ProjectCreationFlow";
import { NewTaskModal } from "@/features/factory/NewTaskModal";
import { CreateWorkspaceModal } from "@/components/CreateWorkspaceModal";
import { SettingsModal } from "@/components/SettingsModal";
import { WorkspaceIconPreview } from "@/components/EmojiIconPicker";

const PROJECT_SUB_ITEMS: { id: ProjectSubTab; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Overview", icon: <Eye size={14} strokeWidth={1.5} /> },
  { id: "board", label: "Board", icon: <LayoutGrid size={14} strokeWidth={1.5} /> },
  { id: "git", label: "Git", icon: <GitBranch size={14} strokeWidth={1.5} /> },
  { id: "documents", label: "Documents", icon: <FileText size={14} strokeWidth={1.5} /> },
  { id: "terminal", label: "Terminal", icon: <Terminal size={14} strokeWidth={1.5} /> },
  { id: "chat", label: "Chat", icon: <MessageSquare size={14} strokeWidth={1.5} /> },
  { id: "secrets", label: "Secrets", icon: <KeyRound size={14} strokeWidth={1.5} /> },
];

type ContentSubItemId = ContentTab | "settings";
const CONTENT_SUB_ITEMS: { id: ContentSubItemId; label: string; icon: React.ReactNode }[] = [
  { id: "board", label: "Board", icon: <LayoutGrid size={14} strokeWidth={1.5} /> },
  { id: "calendar", label: "Calendar", icon: <CalendarDays size={14} strokeWidth={1.5} /> },
  { id: "keywords", label: "Keywords", icon: <Tag size={14} strokeWidth={1.5} /> },
  { id: "stats", label: "Stats", icon: <BarChart3 size={14} strokeWidth={1.5} /> },
  { id: "settings", label: "Settings", icon: <Settings size={14} strokeWidth={1.5} /> },
];

// ─── Nav Item ──────────────────
function NavItem({
  icon,
  label,
  badge,
  active,
  onClick,
  collapsed,
  indent = false,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: number | string;
  active: boolean;
  onClick: () => void;
  collapsed: boolean;
  indent?: boolean;
}) {
  if (collapsed) {
    return (
      <div className="px-1.5">
        <ColonyTooltip content={`${label}${badge !== undefined ? ` (${badge})` : ""}`} side="right" className="w-full">
          <button
            onClick={onClick}
            aria-label={label}
            aria-current={active ? "page" : undefined}
            className={`flex items-center justify-center w-full h-8 rounded-[5px] transition-all duration-150 cursor-pointer
              ${active ? "bg-[rgba(0,178,200,0.08)]" : "hover:bg-[rgba(255,255,255,0.04)]"}
            `}
          >
            <span className={active ? "text-[color:var(--colony-brand)]" : "text-[#555560]"}>
              {icon}
            </span>
          </button>
        </ColonyTooltip>
      </div>
    );
  }

  return (
    <div className="px-1.5">
      <button
        onClick={onClick}
        className={`relative flex items-center gap-2 w-full h-8 rounded-[5px] transition-all duration-150 cursor-pointer group/nav
          ${indent ? "pl-7 pr-2" : "px-2"}
          ${active
            ? "bg-[rgba(0,178,200,0.08)] text-colony-text-primary"
            : "text-colony-text-secondary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)]"
          }
        `}
        style={{ transitionTimingFunction: "var(--ease-out-expo)" }}
      >
        {active && (
          <div className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full" style={{ backgroundColor: "var(--colony-brand)" }} />
        )}
        <span className={`shrink-0 transition-colors ${active ? "text-[color:var(--colony-brand)]" : "text-colony-text-secondary group-hover/nav:text-colony-text-primary"}`}>
          {icon}
        </span>
        <span className={`flex-1 text-left text-[13px] font-medium truncate leading-[1.3] ${active ? "text-colony-text-primary" : "text-colony-text-secondary group-hover/nav:text-colony-text-primary"}`}>
          {label}
        </span>
        {badge !== undefined && (
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-[3px] min-w-[18px] text-center shrink-0
            ${active ? "bg-[color:var(--colony-brand-muted)] text-[color:var(--colony-brand)]" : "bg-[rgba(128,128,128,0.1)] text-colony-text-tertiary"}
          `}>
            {badge}
          </span>
        )}
      </button>
    </div>
  );
}

// ─── Section Header ──────────────────
function SectionHeader({
  label,
  collapsed,
  isOpen,
  onToggle,
  onAdd,
}: {
  label: string;
  collapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onAdd?: () => void;
}) {
  if (collapsed) return null;

  return (
    <div className="flex items-center h-7 px-3 mt-2 group/section">
      <button onClick={onToggle} className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer">
        <ChevronRight
          size={10}
          strokeWidth={2}
          className={`text-colony-text-tertiary transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
        />
        <span className="text-[11px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">
          {label}
        </span>
      </button>
      {onAdd && (
        <button
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          aria-label={`Add ${label.toLowerCase()}`}
          className="opacity-0 group-hover/section:opacity-100 flex items-center justify-center w-4 h-4 rounded-[3px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(128,128,128,0.1)] transition-all cursor-pointer"
        >
          <Plus size={11} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

// ─── Workspace Dropdown ──────────────────
function WorkspaceDropdown({
  collapsed,
  onToggle,
  onNewTask,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onNewTask: () => void;
}) {
  const [wsOpen, setWsOpen] = useState(false);
  const wsRef = useRef<HTMLDivElement>(null);
  const { workspaces, activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore();
  const { user, logout } = useAuth();
  const [showCreateWs, setShowCreateWs] = useState(false);
  const pendingCreate = useRef(false);
  const [, setLocation] = useLocation();
  const { setSelectedProjectSubTab } = useSidebarStore();

  const validTabs = ["overview", "board", "git", "documents", "terminal", "chat", "secrets"] as const;

  const handleSwitchWorkspace = useCallback((targetWsId: string) => {
    // Switch workspace
    setActiveWorkspace(targetWsId);
    setWsOpen(false);

    // Restore saved navigation state for the target workspace
    const savedTab = getWorkspaceTab(targetWsId);
    if (savedTab && (validTabs as readonly string[]).includes(savedTab)) {
      setSelectedProjectSubTab(savedTab as typeof validTabs[number]);
    }

    const savedRoute = getWorkspaceRoute(targetWsId);
    if (savedRoute) {
      setLocation(savedRoute);
    }
  }, [setActiveWorkspace, setLocation, setSelectedProjectSubTab]);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEscapeClose(wsOpen, () => setWsOpen(false));

  if (collapsed) {
    return (
      <div className="px-1.5 shrink-0">
        <div className="h-12 flex items-center">
          <ColonyTooltip content={activeWorkspace?.name ?? "Workspace"} side="right" className="w-full">
            <button onClick={onToggle} className="flex items-center justify-center w-full h-8 rounded-[5px] hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer">
              <img src="/colony-logo.png" alt="Colony" className="w-5 h-5" />
            </button>
          </ColonyTooltip>
        </div>
        <ColonyTooltip content="New Task" side="right" className="w-full">
          <button
            onClick={onNewTask}
            className="flex items-center justify-center w-full h-8 rounded-[5px] text-colony-text-tertiary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
          >
            <SquarePen size={16} strokeWidth={1.5} />
          </button>
        </ColonyTooltip>
      </div>
    );
  }

  return (
    <>
      <div ref={wsRef} className="relative flex items-center gap-1 h-12 px-3 shrink-0">
        <button
          onClick={() => setWsOpen(!wsOpen)}
          aria-expanded={wsOpen}
          aria-haspopup="true"
          aria-label="Workspace selector"
          className={`flex items-center gap-2 flex-1 min-w-0 h-8 px-1 rounded-[6px] hover:bg-[rgba(255,255,255,0.04)] transition-colors cursor-pointer ${wsOpen ? "bg-[rgba(255,255,255,0.04)]" : ""}`}
        >
          <img src="/colony-logo.png" alt="Colony" className="w-5 h-5 shrink-0" />
          <span className="text-[14px] font-semibold text-colony-text-primary tracking-[-0.015em] truncate">
            {activeWorkspace?.name ?? "Colony"}
          </span>
          <ChevronDown size={12} strokeWidth={1.5} className={`ml-auto text-colony-text-tertiary transition-transform duration-200 ${wsOpen ? "rotate-180" : ""}`} />
        </button>

        {/* New Task Button */}
        <ColonyTooltip content="New Task" side="bottom">
          <button
            onClick={onNewTask}
            aria-label="New task"
            className="flex items-center justify-center w-8 h-8 rounded-[6px] text-colony-text-tertiary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer shrink-0"
          >
            <SquarePen size={18} strokeWidth={1.5} />
          </button>
        </ColonyTooltip>

        {/* Workspace Switcher Dropdown */}
        {wsOpen && (
          <div
            role="menu"
            aria-label="Workspace switcher"
            className="absolute top-full left-2 w-[280px] mt-1 z-50 rounded-[10px] overflow-hidden flex flex-col"
            style={{
              background: "var(--colony-surface-elevated, hsl(var(--popover)))",
              border: "1px solid var(--colony-border)",
              boxShadow: "var(--colony-shadow-elevated, var(--shadow-lg))",
              animation: "colony-fadeIn 150ms var(--ease-out-expo)",
            }}
          >
            {/* Current workspace info */}
            <div className="p-3 pb-2">
              <div className="flex items-start gap-3 mb-3">
                <WorkspaceIconPreview
                  icon={activeWorkspace?.icon ?? "colony"}
                  color={activeWorkspace?.color ?? "#6366f1"}
                  size={36}
                  className="rounded-[6px]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-colony-text-primary truncate">{activeWorkspace?.name}</div>
                  {activeWorkspace?.githubLogin ? (
                    <div className="text-[11px] text-colony-text-tertiary truncate">@{activeWorkspace.githubLogin}</div>
                  ) : (
                    <div className="text-[11px] text-colony-text-tertiary">
                      {activeWorkspace && getWorkspaceType(activeWorkspace) === "local" ? "Local" : "Cloud"} workspace
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="h-px mx-3" style={{ background: "var(--colony-border)" }} />

            {/* Workspace list */}
            <div className="py-1.5">
              {user?.email && (
                <div className="px-3 pb-1.5 text-[11px] font-medium text-colony-text-tertiary truncate">{user.email}</div>
              )}
              {workspaces.map((ws) => (
                <button
                  key={ws.id}
                  role="menuitem"
                  onClick={() => handleSwitchWorkspace(ws.id)}
                  className="w-full flex items-center gap-3 px-3 py-1.5 hover:bg-colony-surface-hover transition-colors text-left"
                >
                  <WorkspaceIconPreview
                    icon={ws.icon ?? "colony"}
                    color={ws.color}
                    size={20}
                    className="rounded-[3px]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-[12px] truncate ${ws.id === activeWorkspaceId ? "text-colony-text-primary font-medium" : "text-colony-text-secondary"}`}>
                      {ws.name}
                    </div>
                    {ws.githubLogin ? (
                      <div className="text-[10px] text-colony-text-tertiary truncate">@{ws.githubLogin}</div>
                    ) : null}
                  </div>
                  {ws.id === activeWorkspaceId && <Check size={13} className="text-colony-text-primary shrink-0" />}
                </button>
              ))}
              <button
                onClick={() => { setWsOpen(false); pendingCreate.current = true; setTimeout(() => setShowCreateWs(true), 150); }}
                className="w-full flex items-center gap-3 px-3 py-1.5 hover:bg-colony-surface-hover transition-colors text-left text-colony-text-secondary hover:text-colony-text-primary mt-0.5"
              >
                <div className="w-5 h-5 rounded-[3px] border border-dashed border-colony-border flex items-center justify-center shrink-0">
                  <Plus size={10} />
                </div>
                <span className="text-[12px]">New workspace</span>
              </button>
            </div>

            <div className="h-px mx-3" style={{ background: "var(--colony-border)" }} />

            {/* Account actions */}
            <div className="py-1 pb-2">
              <button
                onClick={() => { setWsOpen(false); logout(); }}
                className="w-full text-left px-3 py-1.5 text-[12px] text-colony-text-secondary hover:bg-colony-surface-hover hover:text-colony-text-primary transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreateWs && (
        <CreateWorkspaceModal isOpen={showCreateWs} onClose={() => setShowCreateWs(false)} />
      )}
    </>
  );
}

// ─── Sidebar Credit Meter ──────────────────
function SidebarCreditMeter({ collapsed }: { collapsed: boolean }) {
  const backendId = useWorkspaceStore((s) => s.getActiveWorkspaceBackendId());
  const credits = useBillingStore((s) => s.credits);
  const subscription = useBillingStore((s) => s.subscription);
  const [, setLocation] = useLocation();

  if (collapsed) return null;

  if (backendId === null || !subscription) {
    return (
      <button onClick={() => setLocation("/billing")} className="block w-full px-3 py-2 hover:bg-[rgba(128,128,128,0.04)] transition-colors">
        <span className="text-[11px] text-colony-text-tertiary">Credits</span>
      </button>
    );
  }

  if (subscription.billingMode === "byok") {
    return (
      <div className="px-3 py-2">
        <button
          onClick={() => setLocation("/billing")}
          className="flex items-center gap-1.5 text-[11px] text-colony-text-tertiary hover:text-colony-text-secondary transition-colors"
        >
          <KeyRound size={11} />
          <span>BYOK Active</span>
        </button>
      </div>
    );
  }

  const balanceCents = credits?.balanceCents ?? 0;
  const balanceDisplay = `$${(balanceCents / 100).toFixed(2)}`;
  let barColor = "var(--colony-green)";
  if (balanceCents < 200) barColor = "var(--colony-red)";
  else if (balanceCents < 1000) barColor = "var(--colony-amber)";

  return (
    <button
      onClick={() => setLocation("/billing")}
      aria-label={`Credits: ${balanceDisplay} - go to billing`}
      className="block w-full px-3 py-2 hover:bg-[rgba(128,128,128,0.04)] transition-colors"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] text-colony-text-tertiary">Credits</span>
        <span className="text-[11px] font-medium text-colony-text-secondary tabular-nums">{balanceDisplay}</span>
      </div>
      <div className="w-full h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(128,128,128,0.1)" }} role="progressbar" aria-valuenow={balanceCents} aria-valuemin={0} aria-valuemax={10000} aria-label="Credit balance">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(5, balanceCents / 100))}%`, background: barColor }} />
      </div>
    </button>
  );
}

// ─── Main Sidebar ──────────────────
export function ColonySidebar() {
  const [location, setLocation] = useLocation();
  const {
    isCollapsed: collapsed,
    projectsExpanded: projectsOpen,
    selectedProjectSubTab: projectSubTab,
    projectOrder,
    reorderingProjects,
    toggleCollapsed,
    setCollapsed,
    toggleProjectsExpanded,
    setSelectedProjectSubTab,
    setProjectOrder,
    setReorderingProjects,
  } = useSidebarStore();

  const rawProjects = useWorldStore((s) => s.projects);

  const projects = useMemo(() => {
    if (projectOrder.length === 0) return rawProjects;
    const orderMap = new Map(projectOrder.map((id, i) => [id, i]));
    return [...rawProjects].sort((a, b) => {
      const aIdx = orderMap.get(a.id) ?? Infinity;
      const bIdx = orderMap.get(b.id) ?? Infinity;
      if (aIdx === Infinity && bIdx === Infinity) return 0;
      return aIdx - bIdx;
    });
  }, [rawProjects, projectOrder]);

  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragOverRef = useRef<number | null>(null);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const itemRectsRef = useRef<DOMRect[]>([]);
  const itemRefsRef = useRef<(HTMLDivElement | null)[]>([]);
  if (itemRefsRef.current.length > projects.length) {
    itemRefsRef.current.length = projects.length;
  }

  const recalcRects = useCallback(() => {
    itemRectsRef.current = itemRefsRef.current.map((el) =>
      el ? el.getBoundingClientRect() : new DOMRect()
    );
  }, []);

  const getHoverIndex = useCallback((clientY: number): number | null => {
    const rects = itemRectsRef.current;
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (r.height === 0) continue;
      const midY = r.top + r.height / 2;
      if (clientY < midY) return i;
    }
    return rects.length > 0 ? rects.length - 1 : null;
  }, []);

  useEffect(() => {
    if (dragFromIndex === null) return;

    const onPointerMove = (e: PointerEvent) => {
      const target = getHoverIndex(e.clientY);
      dragOverRef.current = target;
      setDragOverIndex((prev) => (prev === target ? prev : target));
    };

    const onPointerUp = () => {
      const from = dragFromIndex;
      const to = dragOverRef.current;
      if (from !== null && to !== null && from !== to) {
        const current = projectsRef.current;
        const reordered = [...current];
        const [removed] = reordered.splice(from, 1);
        reordered.splice(to, 0, removed);
        setProjectOrder(reordered.map((p) => p.id));
      }
      setDragFromIndex(null);
      setDragOverIndex(null);
      dragOverRef.current = null;
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [dragFromIndex, setProjectOrder, getHoverIndex]);

  const handlePointerDown = useCallback((e: React.PointerEvent, idx: number) => {
    e.preventDefault();
    recalcRects();
    setDragFromIndex(idx);
    setDragOverIndex(idx);
    dragOverRef.current = idx;
  }, [recalcRects]);

  const exitReorderMode = useCallback(() => {
    setReorderingProjects(false);
    setDragFromIndex(null);
    setDragOverIndex(null);
  }, [setReorderingProjects]);

  useEscapeClose(reorderingProjects, exitReorderMode);

  const { canPreview } = useWorkspaceFeatures();
  const { workspaces, activeWorkspaceId } = useWorkspaceStore();
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);
  const numericWorkspaceId = activeWorkspace?.backendId ?? null;
  const contentActiveTab = useContentStore((s) => s.activeTab);
  const setContentActiveTab = useContentStore((s) => s.setActiveTab);

  const toggleCommandPalette = useUIStore((s) => s.toggleCommandPalette);

  const [showAddProject, setShowAddProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [subItemsOpen, setSubItemsOpen] = useState(true);

  // ─── Per-workspace navigation state persistence ───
  // Keep a ref to the active workspace so effects can read the current value
  // without re-triggering when the workspace itself changes.
  const activeWsIdRef = useRef(activeWorkspaceId);
  activeWsIdRef.current = activeWorkspaceId;

  // Save current route whenever the user navigates (within the same workspace)
  useEffect(() => {
    if (activeWsIdRef.current) {
      setWorkspaceRoute(activeWsIdRef.current, location);
    }
  }, [location]);

  // Save current tab whenever the user switches tabs
  useEffect(() => {
    if (activeWsIdRef.current) {
      setWorkspaceTab(activeWsIdRef.current, projectSubTab);
    }
  }, [projectSubTab]);

  // Derive selected project from URL
  const isProjectsView = location.startsWith("/projects");
  const projectIdMatch = location.match(/\/projects\/(\d+)/);
  const selectedProjectId = projectIdMatch?.[1] ?? null;

  // Determine active nav view from URL
  const activeView = location === "/world" ? "world"
    : location === "/preview" ? "preview"
    : isProjectsView ? "projects"
    : location === "/billing" ? "billing"
    : location === "/agent-builder" ? "agent-builder"
    : "my-agents";

  const width = collapsed ? 56 : 220;
  const tauriActive = isTauri();

  const isContentProject = (projectId: string) => {
    const proj = projects.find((p) => String(p.id) === projectId);
    return proj?.project_category === 'marketing' && proj?.project_subtype === 'content';
  };

  const handleProjectClick = (projectId: string) => {
    if (selectedProjectId === projectId) {
      // Already selected — toggle sub-items
      setSubItemsOpen((v) => !v);
    } else {
      // Marketing/content projects go directly to the content dashboard
      if (isContentProject(projectId)) {
        setLocation(`/projects/${projectId}/content`);
      } else {
        setLocation(`/projects/${projectId}`);
      }
      setSubItemsOpen(true);
    }
  };

  const handleContentSubClick = (projectId: string, subId: ContentSubItemId) => {
    if (subId === "settings") {
      setLocation(`/projects/${projectId}/content/settings`);
    } else {
      setContentActiveTab(subId);
      setLocation(`/projects/${projectId}/content`);
    }
  };

  return (
    <>
      <aside
        aria-label="Main navigation"
        className="flex flex-col shrink-0 h-full select-none transition-[width] duration-200 overflow-hidden"
        style={{
          width,
          background: "var(--gradient-sidebar, hsl(var(--sidebar)))",
          transitionTimingFunction: "var(--ease-out-expo)",
        }}
        {...(tauriActive ? { "data-tauri-drag-region": true } : {})}
      >
        {/* macOS traffic lights spacer */}
        {tauriActive && <div className="h-7 shrink-0" data-tauri-drag-region="true" />}
        {!tauriActive && <div className="h-3 shrink-0" />}

        {/* Header / Workspace Switcher */}
        <WorkspaceDropdown collapsed={collapsed} onToggle={toggleCollapsed} onNewTask={() => setShowNewTask(true)} />

        {/* Search trigger (Cmd+K) */}
        <div className={collapsed ? "px-1.5 mb-1" : "px-3 mb-1"}>
          {collapsed ? (
            <ColonyTooltip content="Search" side="right" shortcut="Cmd+K" className="w-full">
              <button onClick={toggleCommandPalette} className="flex items-center justify-center w-full h-8 rounded-[6px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer">
                <Search size={16} strokeWidth={1.5} />
              </button>
            </ColonyTooltip>
          ) : (
            <button onClick={toggleCommandPalette} className="flex items-center gap-2 w-full h-[30px] px-2 rounded-[6px] text-[12px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer">
              <Search size={14} strokeWidth={1.5} className="shrink-0" />
              <span className="flex-1 text-left">Search</span>
              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-[4px] border border-colony-border-subtle" style={{ background: "rgba(128,128,128,0.06)" }}>
                <span className="text-[10px] text-colony-text-tertiary font-medium">&#x2318;K</span>
              </span>
            </button>
          )}
        </div>

        <div className="h-2" />

        {/* Main Navigation */}
        <nav className="flex flex-col gap-0.5" aria-label="Primary">
          <NavItem
            icon={<Bot size={16} strokeWidth={1.5} />}
            label="My Agents"
            active={activeView === "my-agents"}
            onClick={() => setLocation("/")}
            collapsed={collapsed}
          />
          <NavItem
            icon={<Globe size={16} strokeWidth={1.5} />}
            label="World"
            active={activeView === "world"}
            onClick={() => setLocation("/world")}
            collapsed={collapsed}
          />
          <NavItem
            icon={<Workflow size={16} strokeWidth={1.5} />}
            label="Agent Builder"
            active={activeView === "agent-builder"}
            onClick={() => setLocation("/agent-builder")}
            collapsed={collapsed}
          />
          {canPreview && (
            <NavItem
              icon={<Eye size={16} strokeWidth={1.5} />}
              label="Preview"
              active={activeView === "preview"}
              onClick={() => setLocation("/preview")}
              collapsed={collapsed}
            />
          )}
        </nav>

        {/* Divider */}
        <div className="mx-3 my-2 h-px" style={{ background: "var(--colony-border-subtle, rgba(128,128,128,0.06))" }} />

        {/* Projects Section */}
        <SectionHeader
          label="Projects"
          collapsed={collapsed}
          isOpen={projectsOpen}
          onToggle={toggleProjectsExpanded}
          onAdd={() => setShowAddProject(true)}
        />
        {/* Projects list — expanded */}
        {projectsOpen && !collapsed && (
          <nav className="flex flex-col gap-0.5 mt-0.5 flex-1 overflow-y-auto min-h-0 colony-scrollbar" style={{ scrollbarWidth: "thin" }}>
            {reorderingProjects && (
              <div className="flex items-center justify-between px-3 py-1 mb-0.5">
                <span className="text-[10px] font-medium text-colony-text-tertiary uppercase tracking-wider">Reorder</span>
                <button
                  onClick={exitReorderMode}
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded-[3px] hover:bg-[rgba(255,255,255,0.06)] transition-colors cursor-pointer"
                  style={{ color: "var(--colony-brand)" }}
                >
                  Done
                </button>
              </div>
            )}
            {projects.map((project, idx) => {
              const isSelected = isProjectsView && selectedProjectId === String(project.id);

              return (
                <div key={project.id}>
                  {reorderingProjects ? (
                    <div
                      className="px-1.5 select-none"
                      ref={(el) => { itemRefsRef.current[idx] = el; }}
                      onPointerDown={(e) => handlePointerDown(e, idx)}
                    >
                      {dragOverIndex === idx && dragFromIndex !== null && dragFromIndex !== idx && (
                        <div className="h-[2px] rounded-full mx-2 mb-0.5" style={{ backgroundColor: "var(--colony-brand)" }} />
                      )}
                      <div className={`relative flex items-center gap-2 w-full h-7 px-2 rounded-[5px] transition-all duration-150 cursor-grab active:cursor-grabbing text-colony-text-secondary hover:bg-[rgba(255,255,255,0.06)] border border-transparent group ${dragFromIndex === idx ? "opacity-40" : ""}`}>
                        <GripVertical size={12} className="text-colony-text-tertiary shrink-0" />
                        <span className="text-[13px] font-medium truncate leading-[1.3] text-colony-text-secondary group-hover:text-colony-text-primary">
                          {project.name}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        <div className="px-1.5">
                          <button
                            onClick={() => handleProjectClick(String(project.id))}
                            className={`relative flex items-center gap-2 w-full h-7 px-2 rounded-[5px] transition-all duration-150 cursor-pointer group
                              ${isSelected
                                ? "bg-[color:var(--colony-brand-muted)] text-colony-text-primary border border-[rgba(0,178,200,0.2)]"
                                : "text-colony-text-secondary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)] border border-transparent"
                              }
                            `}
                            style={{ transitionTimingFunction: "var(--ease-out-expo)" }}
                          >
                            {isSelected && (
                              <div className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full" style={{ backgroundColor: "var(--colony-brand)" }} />
                            )}
                            <StatusIcon status={mapProjectStatus(project.status ?? "healthy")} size={12} />
                            <span className={`text-[13px] font-medium truncate leading-[1.3] ${isSelected ? "text-colony-text-primary" : "text-colony-text-secondary group-hover:text-colony-text-primary"}`}>
                              {project.name}
                            </span>
                            {isSelected && (
                              <ChevronDown size={10} strokeWidth={2} className={`text-colony-text-tertiary shrink-0 ml-auto transition-transform duration-150 ${subItemsOpen ? "" : "-rotate-90"}`} />
                            )}
                          </button>
                        </div>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="min-w-[160px]">
                        <ContextMenuItem onClick={() => setReorderingProjects(true)} className="gap-2 cursor-pointer">
                          <ArrowUpDown size={14} />
                          Reorder projects
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  )}

                  {isSelected && subItemsOpen && !reorderingProjects && (
                    <div className="flex flex-col gap-0.5 mt-0.5 mb-1">
                      {isContentProject(String(project.id)) ? (
                        CONTENT_SUB_ITEMS.map((sub) => (
                          <NavItem
                            key={sub.id}
                            icon={sub.icon}
                            label={sub.label}
                            active={sub.id === "settings"
                              ? location.includes("/content/settings")
                              : !location.includes("/content/settings") && contentActiveTab === sub.id}
                            onClick={() => handleContentSubClick(String(project.id), sub.id)}
                            collapsed={collapsed}
                            indent
                          />
                        ))
                      ) : (
                        PROJECT_SUB_ITEMS.map((sub) => (
                          <NavItem
                            key={sub.id}
                            icon={sub.icon}
                            label={sub.label}
                            active={projectSubTab === sub.id}
                            onClick={() => setSelectedProjectSubTab(sub.id)}
                            collapsed={collapsed}
                            indent
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {projects.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-[11px] text-colony-text-tertiary">No projects yet</p>
                <button
                  onClick={() => setShowAddProject(true)}
                  className="mt-2 text-[11px] font-medium hover:underline"
                  style={{ color: "var(--colony-brand)" }}
                >
                  Add project
                </button>
              </div>
            )}
          </nav>
        )}

        {/* Projects list — collapsed */}
        {collapsed && (
          <nav className="flex flex-col gap-0.5 mt-1 flex-1 overflow-y-auto min-h-0" style={{ scrollbarWidth: "none" }}>
            {projects.map((project) => {
              const isSelected = isProjectsView && selectedProjectId === String(project.id);
              return (
                <div key={project.id}>
                  <div className="px-1.5">
                    <ColonyTooltip content={project.name} side="right" className="w-full">
                      <button
                        onClick={() => handleProjectClick(String(project.id))}
                        className={`relative flex items-center justify-center w-full h-8 rounded-[5px] transition-all duration-150 cursor-pointer
                          ${isSelected
                            ? "bg-[rgba(0,178,200,0.08)]"
                            : "hover:bg-[rgba(255,255,255,0.04)]"
                          }
                        `}
                      >
                        {isSelected && (
                          <div className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full" style={{ backgroundColor: "var(--colony-brand)" }} />
                        )}
                        <span className={`text-[12px] font-semibold leading-none ${isSelected ? "text-[color:var(--colony-brand)]" : "text-colony-text-secondary"}`}>
                          {project.name.charAt(0).toUpperCase()}
                        </span>
                      </button>
                    </ColonyTooltip>
                  </div>

                  {/* Collapsed sub-items for selected project */}
                  {isSelected && subItemsOpen && (
                    <div className="flex flex-col gap-0.5 mt-0.5 mb-1">
                      {isContentProject(String(project.id)) ? (
                        CONTENT_SUB_ITEMS.map((sub) => (
                          <NavItem
                            key={sub.id}
                            icon={sub.icon}
                            label={sub.label}
                            active={sub.id === "settings"
                              ? location.includes("/content/settings")
                              : !location.includes("/content/settings") && contentActiveTab === sub.id}
                            onClick={() => handleContentSubClick(String(project.id), sub.id)}
                            collapsed={collapsed}
                          />
                        ))
                      ) : (
                        PROJECT_SUB_ITEMS.map((sub) => (
                          <NavItem
                            key={sub.id}
                            icon={sub.icon}
                            label={sub.label}
                            active={projectSubTab === sub.id}
                            onClick={() => setSelectedProjectSubTab(sub.id)}
                            collapsed={collapsed}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}

        {/* Spacer when projects closed (not collapsed — collapsed has its own flex-1 nav) */}
        {!collapsed && !projectsOpen && <div className="flex-1" />}

        {/* Bottom Section */}
        <div className="mt-auto" style={{ borderTop: "1px solid var(--colony-border-subtle, rgba(128,128,128,0.06))" }}>
          <SidebarCreditMeter collapsed={collapsed} />

          {/* Settings */}
          <NavItem
            icon={<Settings size={16} strokeWidth={1.5} />}
            label="Settings"
            active={false}
            onClick={() => setSettingsOpen(true)}
            collapsed={collapsed}
          />

          {/* User */}
          <SidebarUser collapsed={collapsed} onOpenSettings={() => setSettingsOpen(true)} />

          {/* Collapse toggle */}
          <div className="px-1.5 pb-2">
            {collapsed ? (
              <ColonyTooltip content="Expand sidebar" side="right" className="w-full">
                <button
                  onClick={() => setCollapsed(false)}
                  className="flex items-center justify-center w-full h-7 rounded-[5px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
                >
                  <PanelLeft size={14} strokeWidth={1.5} />
                </button>
              </ColonyTooltip>
            ) : (
              <button
                onClick={() => setCollapsed(true)}
                className="flex items-center gap-2 w-full h-7 px-2 rounded-[5px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
              >
                <PanelLeftClose size={14} strokeWidth={1.5} />
                <span className="text-[11px]">Collapse</span>
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Modals */}
      <NewTaskModal isOpen={showNewTask} onClose={() => setShowNewTask(false)} />
      {showAddProject && (
        <ProjectCreationFlow isOpen={showAddProject} onClose={() => setShowAddProject(false)} />
      )}
      <SettingsModal
        workspaceId={activeWorkspaceId}
        backendWorkspaceId={numericWorkspaceId != null && !isNaN(numericWorkspaceId) ? numericWorkspaceId : null}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />
    </>
  );
}

// ─── User Section ──────────────────
function SidebarUser({ collapsed, onOpenSettings }: { collapsed: boolean; onOpenSettings: () => void }) {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const credits = useBillingStore((s) => s.credits);
  const subscription = useBillingStore((s) => s.subscription);
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const displayName = user?.username ?? "User";
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);
  const planName = subscription?.plan?.displayName ?? "Free";
  const balanceCents = credits?.balanceCents ?? 0;
  const balanceDisplay = balanceCents > 0 ? `$${(balanceCents / 100).toFixed(2)}` : null;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEscapeClose(open, () => setOpen(false));

  const menuItems = [
    { icon: <Settings size={14} strokeWidth={1.5} />, label: "Settings", action: () => { setOpen(false); onOpenSettings(); } },
    { icon: <CreditCard size={14} strokeWidth={1.5} />, label: "Billing", action: () => { setOpen(false); setLocation("/billing"); } },
    { icon: <Workflow size={14} strokeWidth={1.5} />, label: "Agent Builder", action: () => { setOpen(false); setLocation("/agent-builder"); } },
  ];

  const popover = open && (
    <div
      role="menu"
      aria-label="User menu"
      className="absolute bottom-full left-2 right-2 mb-1.5 z-50 rounded-[10px] overflow-hidden"
      style={{
        background: "var(--colony-surface-elevated, hsl(var(--popover)))",
        border: "1px solid var(--colony-border)",
        boxShadow: "var(--colony-shadow-elevated, var(--shadow-lg))",
        animation: "colony-fadeIn 150ms var(--ease-out-expo)",
        minWidth: collapsed ? 240 : undefined,
      }}
    >
      {/* Profile header */}
      <div className="p-3 pb-2.5">
        <div className="flex items-start gap-3">
          <ColonyAvatar name={displayName} size="lg" status="online" />
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="text-[13px] font-semibold text-colony-text-primary truncate">{displayName}</div>
            {user?.email && (
              <div className="text-[11px] text-colony-text-tertiary truncate mt-0.5">{user.email}</div>
            )}
            {activeWorkspace && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[10px] font-medium"
                  style={{
                    background: "var(--colony-brand-muted)",
                    color: "var(--colony-brand)",
                  }}
                >
                  {planName}
                </span>
                {balanceDisplay && (
                  <span className="text-[10px] text-colony-text-tertiary font-medium tabular-nums">
                    {balanceDisplay} credits
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="h-px mx-3" style={{ background: "var(--colony-border)" }} />

      {/* Menu items */}
      <div className="py-1.5">
        {menuItems.map((item) => (
          <button
            key={item.label}
            onClick={item.action}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 text-[12px] text-colony-text-secondary hover:text-colony-text-primary hover:bg-colony-surface-hover transition-colors text-left"
          >
            <span className="text-colony-text-tertiary">{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>

      <div className="h-px mx-3" style={{ background: "var(--colony-border)" }} />

      {/* Log out */}
      <div className="py-1.5 pb-2">
        <button
          onClick={() => { setOpen(false); logout(); }}
          className="w-full flex items-center gap-2.5 px-3 py-1.5 text-[12px] text-red-400 hover:text-red-300 hover:bg-colony-surface-hover transition-colors text-left"
        >
          <LogOut size={14} strokeWidth={1.5} />
          Log out
        </button>
      </div>
    </div>
  );

  if (collapsed) {
    return (
      <div ref={popRef} className="relative px-1.5 py-2">
        <ColonyTooltip content={open ? undefined : displayName} side="right" className="w-full">
          <button
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-haspopup="true"
            aria-label="User menu"
            className={`flex items-center justify-center w-full h-8 rounded-[5px] cursor-pointer transition-all ${open ? "bg-[rgba(255,255,255,0.06)]" : "hover:bg-[rgba(255,255,255,0.04)]"}`}
          >
            <ColonyAvatar name={displayName} size="xs" />
          </button>
        </ColonyTooltip>
        {popover}
      </div>
    );
  }

  return (
    <div ref={popRef} className="relative px-1.5">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="User menu"
        className={`flex items-center gap-2 w-full h-8 px-1.5 rounded-[5px] cursor-pointer transition-all ${open ? "bg-[rgba(255,255,255,0.06)]" : "hover:bg-[rgba(255,255,255,0.04)]"}`}
      >
        <ColonyAvatar name={displayName} size="xs" />
        <span className="text-[12px] font-medium text-colony-text-secondary truncate flex-1 text-left">{displayName}</span>
        <ChevronDown size={12} strokeWidth={1.5} className={`text-colony-text-tertiary transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {popover}
    </div>
  );
}
